import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

// State
import { clearStock, applyStock, clearStockErrors } from '../../state/modules/stock/actions';
import { addSailplayDiscount } from '../../state/modules/cart/actions';
import {
  getConditions,
  getRewards,
  progressSelector,
  allConditionsCheckedSelector,
} from '../../state/modules/stock/selectors';

// Components
import CheckItem from './CheckItem';
import RewardItem from './RewardItem';

// Icons
import ClearIcon from '../../icons/delete.svg';

// Styles
import styles from './PromocodeWidget.styl';

const cx = classNames.bind(styles);

export default function PromocodeWidget(props) {
  const { className, inputClassname, withProgressbar } = props;

  const intl = useIntl();
  const dispatch = useDispatch();

  const {
    isApplying,
    isApplied,
    isClearing,
    error,
    isAllRewardsInStoplist,
    isAllConditionGoodsInStopList,
    data: { code },
  } = useSelector(state => state.stock);
  const conditions = useSelector(getConditions);
  const rewards = useSelector(getRewards);
  const allConditionsChecked = useSelector(allConditionsCheckedSelector);
  const progressValue = useSelector(progressSelector);

  const [promocodeValue, setPromocodeValue] = useState('');

  const hasConditions = conditions.length > 0;
  const hasRewards = rewards.length > 0;

  const onPromocodeChange = e => {
    dispatch(clearStockErrors());
    setPromocodeValue(e.target.value);
  };

  const onPromocodeFocus = () => {
    dispatch(clearStockErrors());
  };

  // сбрасываем промокод, если он применен
  const onClearStock = () => {
    dispatch(clearStock());
    setPromocodeValue('');
  };

  const onApplyStock = async () => {
    if (!promocodeValue) {
      return;
    }
    dispatch(applyStock({ stock_code: promocodeValue }));
  };

  return (
    <div className={cx('PromocodeWidget', className)}>
      {withProgressbar && hasConditions && (
        <div className={cx('PromocodeWidget__progress')}>
          <div
            className={cx('PromocodeWidget__progress-scale')}
            style={{ transform: `scaleX(${progressValue})` }}
          />
        </div>
      )}
      <div className={cx('PromocodeWidget__field', inputClassname)}>
        <input
          className={cx('PromocodeWidget__input', {
            PromocodeWidget__input_applied: isApplying || isApplied,
            PromocodeWidget__input_error: error,
          })}
          value={isApplied ? code : promocodeValue}
          onChange={onPromocodeChange}
          onFocus={onPromocodeFocus}
          type="text"
          placeholder={intl.formatMessage({ id: 'field.promocode.placeholder' })}
          onKeyDown={e => e.key === 'Enter' && !isApplied && onApplyStock()}
          readOnly={isApplied}
        />
        <button
          className={cx('PromocodeWidget__button', {
            PromocodeWidget__button_applied: isApplying || isApplied,
          })}
          onClick={isApplying || isApplied ? onClearStock : onApplyStock}
          disabled={isClearing}
          type="button"
        >
          {!isApplied && !(isApplying || isClearing) && (
            <div className={cx('PromocodeWidget__button-arrow')} />
          )}
          {isApplied && !(isApplying || isClearing) && (
            <div className={cx('PromocodeWidget__button-clear')}>
              <ClearIcon />
            </div>
          )}
          {(isApplying || isClearing) && <div className={cx('PromocodeWidget__spinner')} />}
        </button>
      </div>
      {error && (
        <div className={cx('PromocodeWidget__error')} dangerouslySetInnerHTML={{ __html: error }} />
      )}
      {hasConditions && (
        <div className={cx('PromocodeWidget__conditions')}>
          {conditions.map((condition, index) => (
            <CheckItem
              key={index}
              idx={index}
              code={code}
              {...condition}
              isOutOfStock={isAllConditionGoodsInStopList}
            />
          ))}
        </div>
      )}
      {hasRewards && (
        <div className={cx('PromocodeWidget__conditions')}>
          {rewards.map((reward, index) => (
            <RewardItem
              key={index}
              idx={index}
              {...reward}
              code={code}
              isOutOfStock={isAllRewardsInStoplist || isAllConditionGoodsInStopList}
              allConditionsChecked={allConditionsChecked}
            />
          ))}
        </div>
      )}
    </div>
  );
}

PromocodeWidget.defaultProps = {
  className: '',
  inputClassname: '',
  withProgressbar: true,
};

PromocodeWidget.propTypes = {
  className: PropTypes.string,
  inputClassname: PropTypes.string,
  withProgressbar: PropTypes.bool,
};
